// App.jsx
import React from "react";

import { HelmetProvider, Helmet } from "react-helmet-async";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import createEmotionCache from "./utils/createEmotionCache";
import createTheme from "./theme";
import useTheme from "./hooks/useTheme";
import SplashScreen from "./components/SplashScreen";
import { TabProvider } from "./TabContext";
import { SnackbarProvider } from "@contexts/SnackbarContext";
import { ConfirmDialogProvider } from "@contexts/ConfirmDialogContext";
import { ModalProvider } from "mui-modal-provider";
import { NavigationProvider } from "./contexts/NavigationContext";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { AuthProvider, useAuth } from "@auth/AuthContext";
import CrossTabSessionDialog from "@auth/CrossTabSessionDialog";
import ChangePassword from "./pages/user/ChangePassword";

import ErrorBoundary from "./components/ErrorBoundary";

// IMPORTANT: routes is a function that accepts tenantInfo (can be null/undefined)
import getRoutes from "./routes";
import {
  installClickCapture,
  patternFromMatches,
  setCurrentRoute,
} from "./utils/clientContext";

const clientSideEmotionCache = createEmotionCache();

function AppShell() {
  const { theme } = useTheme();
  const { loading, tenantInfo } = useAuth();

  // ✅ Always call hooks (useMemo) on every render — no early returns before this.
  // getRoutes must handle tenantInfo being null/undefined.
  const router = React.useMemo(() => {
    return createBrowserRouter(getRoutes(tenantInfo));
  }, [tenantInfo]);

  // LWI-1721 — keep the matched route PATTERN (no ids) and the last clicked
  // control available to the axios interceptor for the Audit Log.
  React.useEffect(() => {
    installClickCapture();
    const sync = (state) => setCurrentRoute(patternFromMatches(state.matches));
    sync(router.state);
    return router.subscribe(sync);
  }, [router]);

  const muiTheme = React.useMemo(() => createTheme(theme), [theme]);

  // You can still decide to render Splash conditionally,
  // but do it AFTER all hooks have been called.
  const currentPath = window.location.pathname;
  const isPublicRoute =
    currentPath.startsWith("/login") || currentPath.startsWith("/error");

  if (!isPublicRoute && loading) {
    // This conditional return is fine now, because all hooks above have already run this render.
    return <SplashScreen />;
  }

  return (
    <CacheProvider value={clientSideEmotionCache}>
      <HelmetProvider>
        <Helmet titleTemplate="%s | IQ" defaultTitle="LOGIKweb-IQ" />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MuiThemeProvider theme={muiTheme}>
            {/* LWI-1717 — blocks this tab when the session or tenant changes in another tab. */}
            <CrossTabSessionDialog />
            <NavigationProvider>
              <ConfirmDialogProvider>
                <SnackbarProvider>
                  <ModalProvider>
                    <TabProvider>
                      <ErrorBoundary>
                        <React.Suspense fallback={<SplashScreen />}>
                          {/* LWI-1163 — the v6 `future` opt-in flags
                              (v7_startTransition) are the default on
                              React Router 7; the prop is gone. */}
                          <RouterProvider router={router} />
                        </React.Suspense>
                      </ErrorBoundary>
                    </TabProvider>
                  </ModalProvider>
                </SnackbarProvider>
              </ConfirmDialogProvider>
            </NavigationProvider>
          </MuiThemeProvider>
        </LocalizationProvider>
      </HelmetProvider>
    </CacheProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
